/**
 * RTK Query endpoints for the warehouse Product resource.
 *
 * Covers retrieve, create, patch, remove, and picture upload (multipart).
 * The grid list is served by EntityGrid's dataSource; these endpoints handle
 * write surfaces and the single-product detail fetch used by ProductFormView.
 */

// eslint-disable-next-line no-restricted-imports -- documented queryFn escape hatch for multipart uploads outside axiosBaseQuery
import apiClient from '@sophocles/http-common';

import { entitiesApi } from './entitiesApi';

export const productsApi = entitiesApi.injectEndpoints({
  endpoints: build => ({
    retrieveProduct: build.query({
      query: ({ id }) => ({ url: `products/${id}/` }),
      transformResponse: b => b.data,
      providesTags: (result, error, { id }) => [{ type: 'Product', id }],
    }),

    createProduct: build.mutation({
      query: ({ body }) => ({ url: 'products/', method: 'post', data: body }),
      transformResponse: b => b.data,
      invalidatesTags: [{ type: 'Product', id: 'LIST' }],
    }),

    patchProduct: build.mutation({
      query: ({ id, body }) => ({ url: `products/${id}/`, method: 'patch', data: body }),
      transformResponse: b => b.data,
      invalidatesTags: (result, error, { id }) => [
        { type: 'Product', id: 'LIST' },
        { type: 'Product', id },
      ],
    }),

    removeProduct: build.mutation({
      query: ({ id }) => ({ url: `products/${id}/`, method: 'delete' }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'Product', id: 'LIST' },
        { type: 'Product', id },
      ],
    }),

    // Picture is an ImageField — must be sent as multipart. Axios infers the
    // Content-Type boundary automatically when handed a FormData body.
    updateProductPicture: build.mutation({
      queryFn: async ({ id, file }) => {
        try {
          const formData = new FormData();
          formData.append('picture', file);
          const res = await apiClient.patch(`odl-app/products/${id}/`, formData);
          return { data: res.data };
        } catch (err) {
          return { error: { status: err.response?.status, data: err.response?.data } };
        }
      },
      invalidatesTags: (result, error, { id }) => [{ type: 'Product', id }],
    }),

    // Full list — used by ProductForm's service-product dropdown.
    // Uses queryFn so plain axios params can be forwarded without grid encoding.
    listProducts: build.query({
      queryFn: async ({ params } = {}) => {
        try {
          const res = await apiClient.get('odl-app/products/', { params });
          return { data: res.data?.data ?? res.data };
        } catch (err) {
          return { error: { status: err.response?.status, data: err.response?.data } };
        }
      },
      providesTags: [{ type: 'Product', id: 'LIST' }],
    }),
  }),
});

export const {
  useRetrieveProductQuery,
  useLazyRetrieveProductQuery,
  useCreateProductMutation,
  usePatchProductMutation,
  useRemoveProductMutation,
  useUpdateProductPictureMutation,
  useListProductsQuery,
  useLazyListProductsQuery,
} = productsApi;
